/**
 * Cushique Product Customiser
 *
 * Handles interactivity for the bespoke cushion customiser:
 *  - Shape selection → filters size dropdown to matching optgroup
 *  - Custom size input reveal
 *  - Fabric swatch selection
 *  - Fill card selection
 *  - Live summary bar
 *  - Injects line item properties into the Horizon product form on submit
 *  - Validates required fields before add-to-cart
 */

(function () {
  'use strict';

  class CushiqueCustomiser {
    constructor(el) {
      this.el = el;
      this.formId = el.dataset.formId;

      this.shapeInputs   = el.querySelectorAll('[name="cq-shape"]');
      this.sizeSelect    = el.querySelector('#cq-size-select');
      this.customSizePanel = el.querySelector('#cq-custom-size');
      this.customWidthInput  = el.querySelector('#cq-custom-width');
      this.customHeightInput = el.querySelector('#cq-custom-height');
      this.fabricInputs  = el.querySelectorAll('[name="cq-fabric"]');
      this.fillInputs    = el.querySelectorAll('[name="cq-fill"]');

      this.summaryShape  = el.querySelector('#cq-summary-shape');
      this.summarySize   = el.querySelector('#cq-summary-size');
      this.summaryFabric = el.querySelector('#cq-summary-fabric');
      this.summaryFill   = el.querySelector('#cq-summary-fill');
      this.fabricNameEl  = el.querySelector('#cq-fabric-name');

      this.init();
    }

    init() {
      this.bindShapeChange();
      this.bindSizeChange();
      this.bindFabricChange();
      this.bindFillChange();
      this.hookProductForm();
      this.updateSummary();
    }

    // ── Shape ────────────────────────────────────────────────────────────────

    bindShapeChange() {
      this.shapeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.updateShapeSelection(input.value);
          this.updateSummary();
        });
      });
    }

    updateShapeSelection(shape) {
      // Update button styles
      this.el.querySelectorAll('.cq-option-btn--shape').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.querySelector('input').value === shape);
      });

      // Filter the size dropdown: enable only the matching optgroup
      if (!this.sizeSelect) return;
      const groups = this.sizeSelect.querySelectorAll('optgroup');
      groups.forEach((group) => {
        const matches = group.label === shape;
        group.disabled = !matches;
        // Show/hide via hidden attribute (browsers ignore hidden on optgroup, but JS reads it)
        group.hidden = !matches;
      });

      // Select the first option of the now-active group
      const activeGroup = this.sizeSelect.querySelector(`optgroup[label="${shape}"]`);
      if (activeGroup) {
        const firstOption = activeGroup.querySelector('option');
        if (firstOption) this.sizeSelect.value = firstOption.value;
      }

      // Hide custom size panel on shape change
      if (this.customSizePanel) this.customSizePanel.hidden = true;
    }

    // ── Size dropdown ─────────────────────────────────────────────────────────

    bindSizeChange() {
      if (!this.sizeSelect) return;
      this.sizeSelect.addEventListener('change', () => {
        const isCustom = this.sizeSelect.value === 'Custom';
        if (this.customSizePanel) this.customSizePanel.hidden = !isCustom;
        this.updateSummary();
      });
    }

    // ── Fabric swatches ───────────────────────────────────────────────────────

    bindFabricChange() {
      this.fabricInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.el.querySelectorAll('#cq-fabric-field .cq-swatch').forEach((swatch) => {
            swatch.classList.toggle('is-selected', swatch.querySelector('input') === input);
          });
          if (this.fabricNameEl) this.fabricNameEl.textContent = `— ${input.value}`;
          this.updateSummary();
        });
      });
    }

    // ── Fill cards ────────────────────────────────────────────────────────────

    bindFillChange() {
      this.fillInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.el.querySelectorAll('.cq-fill-card').forEach((card) => {
            card.classList.toggle('is-selected', card.querySelector('input') === input);
          });
          this.updateSummary();
        });
      });
    }

    // ── Live summary ──────────────────────────────────────────────────────────

    updateSummary() {
      const shape  = this.getSelected('cq-shape');
      const size   = this.getSelectedSize();
      const fabric = this.getSelected('cq-fabric');
      const fill   = this.getSelected('cq-fill');

      if (this.summaryShape)  this.summaryShape.textContent  = shape  || '—';
      if (this.summarySize)   this.summarySize.textContent   = size   || '—';
      if (this.summaryFabric) this.summaryFabric.textContent = fabric || '—';
      if (this.summaryFill)   this.summaryFill.textContent   = fill   || '—';
    }

    getSelected(name) {
      const input = this.el.querySelector(`[name="${name}"]:checked`);
      return input ? input.value : null;
    }

    getSelectedSize() {
      if (!this.sizeSelect) return null;
      const val = this.sizeSelect.value;
      if (val === 'Custom') {
        const w = this.customWidthInput && this.customWidthInput.value;
        const h = this.customHeightInput && this.customHeightInput.value;
        return (w && h) ? `${w}"×${h}" (Custom)` : 'Custom';
      }
      return val || null;
    }

    // ── Form injection ────────────────────────────────────────────────────────

    hookProductForm() {
      const attach = () => {
        const form = document.getElementById(this.formId);
        if (!form) return;
        form.addEventListener('submit', (e) => this.onFormSubmit(e));
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', attach);
      } else {
        let attempts = 0;
        const poll = setInterval(() => {
          const form = document.getElementById(this.formId);
          if (form) { clearInterval(poll); attach(); }
          if (++attempts > 20) clearInterval(poll);
        }, 100);
      }
    }

    onFormSubmit(e) {
      if (!this.validate()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      this.injectProperties();
    }

    validate() {
      let valid = true;

      const required = [
        { name: 'cq-shape',  fieldId: 'cq-shape-field',  msg: 'Please select a shape.'     },
        { name: 'cq-fabric', fieldId: 'cq-fabric-field', msg: 'Please select a fabric.'    },
        { name: 'cq-fill',   fieldId: 'cq-fill-field',   msg: 'Please select a fill type.' },
      ];

      required.forEach(({ name, fieldId, msg }) => {
        const field   = this.el.querySelector(`#${fieldId}`);
        const errorEl = field ? field.querySelector('.cq-field__error') : null;
        const selected = this.el.querySelector(`[name="${name}"]:checked`);
        if (!selected) {
          valid = false;
          if (errorEl) { errorEl.hidden = false; errorEl.textContent = msg; }
          if (field) field.classList.add('has-error');
        } else {
          if (errorEl) errorEl.hidden = true;
          if (field) field.classList.remove('has-error');
        }
      });

      // Size validation
      const sizeField = this.el.querySelector('#cq-size-field');
      const sizeError = sizeField ? sizeField.querySelector('.cq-field__error') : null;
      const sizeVal   = this.sizeSelect ? this.sizeSelect.value : null;

      if (!sizeVal) {
        valid = false;
        if (sizeError) { sizeError.hidden = false; sizeError.textContent = 'Please select a size.'; }
        if (sizeField) sizeField.classList.add('has-error');
      } else if (sizeVal === 'Custom') {
        const w = this.customWidthInput && this.customWidthInput.value;
        const h = this.customHeightInput && this.customHeightInput.value;
        if (!w || !h) {
          valid = false;
          if (sizeError) { sizeError.hidden = false; sizeError.textContent = 'Please enter your custom dimensions.'; }
          if (sizeField) sizeField.classList.add('has-error');
        } else {
          if (sizeError) sizeError.hidden = true;
          if (sizeField) sizeField.classList.remove('has-error');
        }
      } else {
        if (sizeError) sizeError.hidden = true;
        if (sizeField) sizeField.classList.remove('has-error');
      }

      if (!valid) {
        const firstError = this.el.querySelector('.has-error');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return valid;
    }

    injectProperties() {
      const form = document.getElementById(this.formId);
      if (!form) return;

      form.querySelectorAll('[data-cq-property]').forEach((el) => el.remove());

      const props = [
        { key: 'Shape',  value: this.getSelected('cq-shape')  },
        { key: 'Size',   value: this.getSelectedSize()         },
        { key: 'Fabric', value: this.getSelected('cq-fabric') },
        { key: 'Fill',   value: this.getSelected('cq-fill')   },
      ];

      props.forEach(({ key, value }) => {
        if (!value) return;
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = `properties[${key}]`;
        input.value = value;
        input.setAttribute('data-cq-property', '');
        form.appendChild(input);
      });
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('.cushique-customiser').forEach((el) => {
      if (!el.dataset.cqInit) {
        new CushiqueCustomiser(el);
        el.dataset.cqInit = '1';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
